import { useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Button, Typography } from '@mui/material';
import moment from 'moment';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import PageLayout from '../../shared/PageLayout';
import { ConfirmFollowUpActionModal } from './ConfirmFollowUpActionModal';
import { FollowUpActionForm, FollowUpActionMode, FollowUpActionSchema } from './FollowUpActionForm';

//this can change
export type FollowUpActionType = z.infer<typeof FollowUpActionSchema> & { id?: string; delete?: boolean };

const useStyles = makeStyles()((theme) => {
    const { spacing } = theme;

    return {
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: `${spacing(3)} ${spacing(2)}`,
            textTransform: 'none',
            backgroundColor: 'white'
        },
        assignContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            marginBottom: spacing(13), //if we have many fuas put margin so that the bottom bar doesn't block it
            padding: spacing(2),
            display: 'flex',
            flexDirection: 'column'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        addFUAButton: {
            textTransform: 'none',
            paddingLeft: spacing(2),
            alignSelf: 'flex-start'
        },
        fuasListContainer: {
            margin: `${spacing(1)} ${spacing(2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        }
    };
});

interface FollowUpActionsProps {
    onGoBack: () => void;
    onSubmit: () => void;
}

export const FollowUpActions = (props: FollowUpActionsProps) => {
    const { onSubmit, onGoBack } = props;
    const { classes } = useStyles();

    const [fuas, setFuas] = useState<FollowUpActionType[]>([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showAddFollowUp, setShowAddFollowUp] = useState(false);
    const [mode, setMode] = useState<FollowUpActionMode>('add');
    const [initialFollowUpAction, setInitialFollowUpAction] = useState<z.infer<typeof FollowUpActionSchema>>();

    const handleOpenConfirmation = () => {
        setShowConfirmation(true);
    };

    const handleCloseConfirmation = () => {
        setShowConfirmation(false);
    };

    const handleSubmit = () => {
        //TODO: https://zenput.atlassian.net/browse/ZP-16297 here we have to add the data coming from fuas array
        setShowConfirmation(false);
        onSubmit();
    };

    const handleEditFollowUpAction = (fua: z.infer<typeof FollowUpActionSchema>) => {
        setInitialFollowUpAction(fua);
        setMode('edit');
        setShowAddFollowUp(true);
    };

    const handleAddFollowUpAction = () => {
        setInitialFollowUpAction(null);
        setMode('add');
        setShowAddFollowUp(true);
    };

    const handleFollowUpAction = (fua: FollowUpActionType) => {
        if (mode === 'add') {
            const newAction = { ...fua, id: crypto.randomUUID() };
            setFuas((prevFuas) => [...prevFuas, newAction]);
        } else if (fua.delete) {
            setFuas((prev) => prev.filter((item) => item.id !== fua.id));
        } else {
            setFuas((prevFuas) => prevFuas.map((el) => (el.id === fua.id ? { ...el, ...fua } : el)));
        }
        setShowAddFollowUp(false);
    };

    return (
        <>
            <PageLayout>
                {{
                    appBar: <AppBar title={_t('mobile_mobileform_follow_up_actions')} onGoBack={onGoBack} />,
                    content: (
                        <div className={classes.assignContainer}>
                            <Typography sx={{ fontWeight: 'medium', color: 'black' }} variant="subtitle1">
                                {_t('mobile_mobileform_follow_up_actions_assign_title')}
                            </Typography>
                            <Typography sx={{ fontWeight: 'normal', marginBottom: 4 }} variant="caption">
                                {_t('mobile_mobileform_follow_up_actions_assign_subtitle')}
                            </Typography>

                            {fuas.map((fua) => {
                                const moment_date = moment(fua.dueDate)
                                    .tz(moment.tz.guess())
                                    .format('ddd D, MMM YYYY, h:mma z');
                                return (
                                    <Box
                                        key={fua.title}
                                        className={classes.fuasListContainer}
                                        onClick={() => handleEditFollowUpAction(fua)}
                                    >
                                        <Box>
                                            <Typography
                                                sx={{ fontWeight: 'normal', color: 'black' }}
                                                variant="subtitle1"
                                            >
                                                {fua.title}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 'normal' }} variant="body2">
                                                {moment_date}
                                            </Typography>
                                        </Box>
                                        <KeyboardArrowRightIcon sx={{ color: '#1976d2', fontSize: 30 }} />
                                    </Box>
                                );
                            })}

                            <Button
                                className={classes.addFUAButton}
                                sx={{ marginTop: fuas.length > 0 ? 4 : 0 }}
                                onClick={handleAddFollowUpAction}
                            >
                                {_t('mobile_mobileform_follow_up_actions_add_button')}
                            </Button>
                        </div>
                    ),
                    bottomBar: (
                        <BottomBar>
                            <div className={classes.bottomBar}>
                                <Button
                                    className={classes.bottomButton}
                                    disabled={fuas.length === 0}
                                    variant="contained"
                                    color="primary"
                                    onClick={onSubmit}
                                >
                                    {_t('mobile_mobileform_follow_up_actions_assign_button')}
                                </Button>
                                <Button
                                    className={classes.bottomButton}
                                    disabled={fuas.length > 0}
                                    onClick={handleOpenConfirmation}
                                >
                                    {_t('mobile_mobileform_follow_up_actions_without_assign_button')}
                                </Button>
                            </div>
                        </BottomBar>
                    )
                }}
            </PageLayout>

            <FollowUpActionForm
                open={showAddFollowUp}
                onGoBack={() => setShowAddFollowUp(false)}
                mode={mode}
                initialFollowUpAction={initialFollowUpAction}
                handleFollowUpAction={handleFollowUpAction}
            />

            <ConfirmFollowUpActionModal
                onSubmit={handleSubmit}
                showConfirmation={showConfirmation}
                handleCloseConfirmation={handleCloseConfirmation}
                _t={_t}
            />
        </>
    );
};
